<template>
  <header class="sticky top-0 z-50 border-b border-gray-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)] backdrop-blur-sm">
    <div class="mx-auto w-full px-3 sm:px-4 lg:px-6">
      <div class="flex flex-wrap items-center gap-3 py-3 lg:min-h-[4.75rem] lg:flex-nowrap lg:py-0 xl:min-h-20">
        <div class="flex min-w-0 items-center gap-3">
          <button
            type="button"
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50 lg:hidden"
            @click="open = !open"
          >
            <svg v-if="!open" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>

            <svg v-else class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <router-link to="/home" class="flex shrink-0 items-center">
            <img
              v-if="logoUrl"
              class="brand-logo object-contain"
              :src="logoUrl"
            />

            <img
              v-else-if="currentUser.lzv2 && currentUser.lzv2.cliente_novavida"
              class="brand-logo object-contain"
              src="@/assets/logo-nova.png"
            />

            <h1 v-else class="select-none text-lg font-bold tracking-tight text-gray-800 sm:text-xl lg:text-[24px]">
              BestMessage V2
            </h1>
          </router-link>
        </div>

        <nav
          class="header-nav-scroll hidden min-w-0 flex-1 justify-start px-1 lg:flex xl:px-4 2xl:justify-center 2xl:px-8"
          :class="{ 'header-nav-dropdown-open': dropRelatorios }"
        >
          <ul class="flex min-w-max flex-nowrap items-center justify-start gap-1 whitespace-nowrap text-sm font-medium text-gray-600 2xl:justify-center 2xl:text-base">
            <li v-for="item in visibleNavItems" :key="item.to">
              <router-link :to="item.to" class="menu-link" active-class="menu-link-active" @click.native="closeMenus">
                {{ item.label }}
              </router-link>
            </li>

            <li v-if="canShowRelatorios" class="relative">
              <button
                type="button"
                class="menu-link"
                :class="{ 'menu-link-active': isRelatoriosAtivo }"
                @click="toggleRelatorios"
              >
                Relatórios
              </button>

              <div
                v-if="dropRelatorios"
                class="absolute right-0 top-12 z-50 w-72 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
              >
                <nav class="max-h-96 overflow-y-auto py-2">
                  <router-link
                    v-for="item in visibleRelatorioItems"
                    :key="item.to"
                    :to="item.to"
                    class="dropdown-link"
                    :class="{ 'dropdown-link-active': $route.path === item.to }"
                    @click.native="closeMenus"
                  >
                    {{ item.label }}
                  </router-link>
                </nav>
              </div>
            </li>
          </ul>
        </nav>

        <div class="ml-auto flex min-w-0 shrink-0 items-center justify-end gap-2 sm:gap-3 lg:gap-2 xl:gap-3 2xl:gap-4">
          <span
            v-if="$store.state.saldoPrePago.ativo && currentUser.admin"
            class="hidden max-w-[8.5rem] truncate rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm xl:block 2xl:max-w-none"
          >
            Saldo: {{ $store.state.saldoPrePago.credito }}
          </span>

          <router-link to="/user" class="user-button hidden lg:flex">
            <div class="user-avatar">
              {{ userInitial }}
            </div>

            <span class="user-name truncate text-sm font-medium text-gray-700">
              {{ currentUser.nome }}
            </span>
          </router-link>

          <button
            type="button"
            class="logout-button hidden lg:flex"
            aria-label="Sair"
            title="Sair"
            @click="logout"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="logout-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1" />
            </svg>
          </button>
        </div>
      </div>

      <div v-if="open" class="border-t border-gray-100 py-4 lg:hidden">
        <nav class="grid gap-2">
          <router-link
            v-for="item in visibleNavItems"
            :key="`mobile-${item.to}`"
            :to="item.to"
            class="mobile-menu-link"
            @click.native="closeMenus"
          >
            {{ item.label }}
          </router-link>

          <button
            v-if="canShowRelatorios"
            type="button"
            class="mobile-menu-link w-full text-left"
            :class="{ 'mobile-menu-link-active': isRelatoriosAtivo }"
            @click="dropRelatorios = !dropRelatorios"
          >
            Relatórios
          </button>

          <div
            v-if="dropRelatorios"
            class="grid gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-2"
          >
            <router-link
              v-for="item in visibleRelatorioItems"
              :key="`mobile-${item.to}`"
              :to="item.to"
              class="mobile-submenu-link"
              :class="{ 'mobile-submenu-link-active': $route.path === item.to }"
              @click.native="closeMenus"
            >
              {{ item.label }}
            </router-link>
          </div>
        </nav>

        <div class="mt-4 flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
          <router-link to="/user" class="flex min-w-0 items-center gap-3" @click.native="closeMenus">
            <div class="user-avatar">
              {{ userInitial }}
            </div>

            <span class="user-name truncate text-sm font-medium text-gray-700">
              {{ currentUser.nome }}
            </span>
          </router-link>

          <button
            type="button"
            class="logout-button inline-flex"
            aria-label="Sair"
            title="Sair"
            @click="logout"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="logout-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  data() {
    return {
      open: false,
      dropRelatorios: false,
      relatorioItems: [
        { to: '/relatorios/relatorio-envio-clientes', label: 'Envios clientes', special: 'enviosClientes' },
        { to: '/relatorios/relatorio-meireles', label: 'Relatório Meireles', special: 'meireles' },
        { to: '/relatorios/rastrear-envio', label: 'Rastrear envio', access: 'rastrear' },
        { to: '/respostas', label: 'Respostas', admin: true },
        { to: '/relatorios/resumo-envios', label: 'Relatório resumo envios', access: 'relatorio_campanha' },
        { to: '/relatorios/validacao', label: 'Relatório validação', access: 'relatorio_validacao' },
        { to: '/relatorios/envio', label: 'Relatório envio', access: 'relatorio_campanha' },
        { to: '/relatorios/analitico', label: 'Relatório analítico', access: 'relatorio_campanha' },
        { to: '/relatorios/chats', label: 'Relatório chats', access: 'relatorio_campanha' },
        { to: '/relatorios/chats-finalizados', label: 'Relatório chats finalizados', access: 'relatorio_campanha' },
        { to: '/relatorios/diario', label: 'Relatório diário', access: 'relatorio_campanha' },
        { to: '/relatorios/sessao', label: 'Relatório sessão', access: 'relatorio_campanha' },
        { to: '/relatorios/auditoria', label: 'Relatório auditoria', admin: true },
        { to: '/relatorios/faturamento', label: 'Faturamento', admin: true },
        { to: '/relatorios/faturamento-clientes', label: 'Faturamento clientes', special: 'faturamentoClientesBluesend' },
      ],
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.user || {};
    },

    logoUrl() {
      const logo = this.$store.state.logo || {};
      if (!logo.filename) return null;

      const params = new URLSearchParams();
      params.set('mimetype', logo.mimetype || 'image/jpeg');
      params.set('filename', logo.filename);
      params.set('folder', logo.folder || 'arquivosWhats');

      return `${this.api}/upload?${params.toString()}`;
    },

    currentAcessos() {
      return Array.isArray(this.currentUser.acessos_lzv2)
        ? this.currentUser.acessos_lzv2
        : [];
    },

    userLzv2() {
      return this.currentUser.lzv2 || {};
    },

    visibleNavItems() {
      return [
        { to: '/home', label: 'Home', show: this.hasAccess('relatorio_chats') },
        { to: '/campanhas', label: 'Campanhas', show: this.hasAccess('campanhas') },
        { to: '/temzap', label: 'Validação', show: this.hasAccess('validacao') },
        { to: '/chat', label: 'Chat', show: this.hasAccess('chats') },
        { to: '/avaliacoes', label: 'Avaliações', show: this.currentUser.operador && this.hasAccess('chats') },
        { to: '/operadores', label: 'Operadores', show: !this.currentUser.supervisor && (this.currentUser.admin || this.currentUser.visualizarChat) },
        { to: '/centros', label: 'Centros de custo', show: this.currentUser.admin && this.userLzv2.centrocusto },
        { to: '/config', label: 'Configurações', show: this.canShowConfig },
        { to: '/tag', label: 'Tabulação', show: this.userLzv2.cliente_novavida },
        { to: '/numerosproibido', label: 'Números proibidos', show: this.userLzv2.cliente_novavida },
        { to: '/nova-conta-bluesend', label: 'Gestão de contas', show: this.canShowCriarContaBluesend },
        { to: '/nova-conta-bvtelecom', label: 'Gestão de contas', show: this.canShowCriarContaBVTelecom },
        { to: '/suporte', label: 'Suporte', show: true },
      ].filter((item) => item.show);
    },

    visibleRelatorioItems() {
      return this.relatorioItems.filter((item) => this.canViewRelatorio(item));
    },

    canShowRelatorios() {
      return this.visibleRelatorioItems.length > 0;
    },

    canViewFaturamentoClientesBluesend() {
      const master = '66eac8eef58389f70fad372d';
      const user = this.currentUser;
      return !!user.admin && ((!user.operador && this.currentUserId === master) ||
        (user.operador && String(user.usuario) === master));
    },

    canShowConfig() {
      return !!this.currentUser.admin && !this.currentUser.supervisor;
    },

    canShowCriarContaBluesend() {
      const allowedAccountIds = [
        '66eac8eef58389f70fad372d',
        
      ];
      const parentUserId = this.currentUser.usuario
        ? this.currentUser.usuario.toString()
        : '';

      return !this.currentUser.supervisor && (
        allowedAccountIds.indexOf(this.currentUserId) !== -1 ||
        (this.currentUser.operador && this.currentUser.admin && allowedAccountIds.indexOf(parentUserId) !== -1)
      );
    },

    canShowCriarContaBVTelecom() {
      const ids = ['6a675909cef45d1dc0e482d3'];
      const userId = this.currentUserId;
      const parentId = this.currentUser.usuario ? this.currentUser.usuario.toString() : '';
      return !this.currentUser.supervisor && (ids.indexOf(userId) !== -1 ||
        (this.currentUser.operador && this.currentUser.admin && ids.indexOf(parentId) !== -1));
    },

    currentUserId() {
      return this.currentUser._id ? this.currentUser._id.toString() : '';
    },

    canViewEnviosClientes() {
      const allowedIds = [
        '626a87fec9cf2abcd1265acf',
        '63fe25095bd52da93a558322',
        '677bef54e735894ed49a2fe4',
        '690ce44750612ed5cb237b15',
      ];

      return !!this.currentUser.admin && allowedIds.indexOf(this.currentUserId) !== -1;
    },

    canViewRelatorioMeireles() {
      return !!this.currentUser.admin && this.currentUserId === '677bef54e735894ed49a2fe4';
    },

    isRelatoriosAtivo() {
      return this.$route.path.startsWith('/relatorios') || this.$route.path === '/respostas';
    },

    userInitial() {
      const nome = (this.currentUser.nome || '').trim();

      if (!nome) return '?';

      return nome
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();
    },
  },

  watch: {
    '$route.path'() {
      this.open = false;
      this.dropRelatorios = false;
    },
  },

  methods: {
    hasAccess(access) {
      return this.currentAcessos.indexOf(access) !== -1;
    },

    toggleRelatorios() {
      this.dropRelatorios = !this.dropRelatorios;
      this.open = false;
    },

    canViewRelatorio(item) {
      if (item.special === 'faturamentoClientesBluesend') {
        return this.canViewFaturamentoClientesBluesend;
      }

      if (item.special === 'enviosClientes') {
        return this.canViewEnviosClientes;
      }

      if (item.special === 'meireles') {
        return this.canViewRelatorioMeireles;
      }

      if (item.admin) {
        return !!this.currentUser.admin;
      }

      if (item.access) {
        return !!this.currentUser.admin || this.currentAcessos.indexOf(item.access) !== -1;
      }

      return true;
    },

    closeMenus() {
      this.open = false;
      this.dropRelatorios = false;
    },

    async logout() {
      // Operador: rota de sessão (grava o horário de saída e revoga só o login dele).
      // Conta: revoga os logins da conta. Se o servidor não responder, sai neste aparelho mesmo assim.
      try {
        if (this.currentUser && this.currentUser.operador) {
          await this.$http.post('/v1/operador/sessao/logout', { id: this.currentUser._id });
        } else {
          await this.$http.post('/v1/users/logout');
        }
      } catch (error) {
        this.$vToastify.info('Saiu deste aparelho, mas nao foi possivel encerrar a sessao no servidor.');
      }

      this.$store.commit('logout');
      this.$router.push({ path: '/login' }).catch(() => {});
    },
  },
};
</script>

<style>
.brand-logo {
  height: 2.5rem;
  width: auto;
  max-width: 170px;
}

@media (min-width: 640px) {
  .brand-logo {
    height: 3rem;
    max-width: 220px;
  }
}

@media (min-width: 1024px) {
  .brand-logo {
    height: 3rem;
    max-width: 210px;
  }
}

@media (min-width: 1280px) {
  .brand-logo {
    height: 3.5rem;
    max-width: 260px;
  }
}

.menu-link {
  display: inline-flex;
  flex-shrink: 0;
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem;
  color: #4b5563;
  font-weight: 500;
  transition: all 300ms ease;
}

.menu-link:hover {
  transform: translateY(-1px);
  background-color: #ffffff;
  color: #111827;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.menu-link-active {
  background-color: #f3f4f6;
  color: #111827;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.dropdown-link {
  display: block;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  color: #374151;
  transition: all 200ms ease;
}

.dropdown-link:hover {
  background-color: #f9fafb;
  color: #111827;
}

.dropdown-link-active {
  background-color: #f3f4f6;
  font-weight: 500;
  color: #111827;
}

.mobile-menu-link {
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  transition: all 200ms ease;
}

.mobile-menu-link:hover,
.mobile-menu-link-active {
  background-color: #f3f4f6;
  color: #111827;
}

.mobile-submenu-link {
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #374151;
  transition: all 200ms ease;
}

.mobile-submenu-link:hover,
.mobile-submenu-link-active {
  background-color: #ffffff;
  font-weight: 500;
  color: #111827;
}

.user-button {
  min-width: 0;
  max-width: 9.5rem;
  cursor: pointer;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.75rem;
  padding: 0.5rem 0.55rem;
  transition: all 300ms ease;
}

.user-name {
  max-width: 5.75rem;
}

.user-button:hover {
  background-color: #f9fafb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.user-avatar {
  display: flex;
  height: 2.5rem;
  width: 2.5rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid #ffffff;
  background: linear-gradient(135deg, #d1d5db, #9ca3af);
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.logout-button {
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 0;
  background: transparent;
  padding: 0;
  color: #ef4444;
  cursor: pointer;
}

.logout-button:focus-visible {
  border-radius: 0.5rem;
  outline: 2px solid rgba(239, 68, 68, 0.35);
  outline-offset: 4px;
}

.logout-icon {
  flex-shrink: 0;
  width: 1.4rem;
  height: 1.4rem;
  color: #ef4444;
  transform-origin: center;
  transition: color 220ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.logout-icon path {
  transition: stroke-width 260ms ease;
}

.logout-button:hover .logout-icon,
.logout-button:focus-visible .logout-icon {
  color: #dc2626;
  transform: translateX(4px) scale(1.12) rotate(-3deg);
}

.logout-button:hover .logout-icon path,
.logout-button:focus-visible .logout-icon path {
  stroke-width: 2.25;
}

@media (min-width: 1024px) and (max-width: 1279px) {
  .menu-link {
    padding: 0.45rem 0.55rem;
    font-size: 0.8125rem;
  }

  .user-button {
    max-width: 7rem;
    gap: 0.375rem;
    padding: 0.45rem 0.4rem;
  }

  .user-name {
    max-width: 3.5rem;
    font-size: 0.8125rem;
  }

  .user-avatar {
    height: 2.25rem;
    width: 2.25rem;
    font-size: 0.75rem;
  }

  .logout-button {
    width: 2.5rem;
    height: 2.5rem;
  }
}

@media (min-width: 1536px) {
  .user-button {
    max-width: 14rem;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
  }

  .user-name {
    max-width: 10rem;
  }
}

.header-nav-scroll {
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.header-nav-scroll::-webkit-scrollbar {
  display: none;
}

.header-nav-scroll.header-nav-dropdown-open {
  overflow: visible;
}
</style>
